import { EntityState, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { round } from 'lodash';

import { deconstructCropData } from 'utils/crop_data';
import { clampCropPanToLimit, fitImageToNode } from 'utils/pan_util';
import canvases from 'domains/canvases';
import photoModule from 'domains/photos';
import { notNull } from 'utils/type_guards';

import cropsAdapter from './crops.adapter';
import utils from './crops.utils';
import actions from './crops.actions';

import type {
  CreateCropPayload,
  CropDimensionsPayload,
  CropEntity,
  CropPhotoLoadedPayload,
  CropsSliceInitialState,
  FilterCropPayload,
  PanCropPayload,
  RestoreArrangementCropsPayload,
  RotateCropPayload,
  ZoomCropPayload,
} from './crops.types';

const initialState: CropsSliceInitialState = {
  activeNodeUid: null,
};

function cropIdsForPhotoId(entities: EntityState<CropEntity>['entities'], photoId: number) {
  return Object.values(entities)
    .filter((crop: CropEntity) => crop.photoId === photoId)
    .map((crop: CropEntity) => crop.id);
}

const crops = createSlice({
  name: 'crops',
  initialState: cropsAdapter.getInitialState(initialState),
  reducers: {
    setActiveImageNode(state, { payload }: PayloadAction<string>) {
      return {
        ...state,
        activeNodeUid: payload,
      };
    },
    createCrop(state, { payload }: PayloadAction<CreateCropPayload>) {
      const { canvasId, photoId, nodeUid } = payload;

      const cropId = utils.buildCropId(canvasId, nodeUid);

      return cropsAdapter.setOne(state, {
        id: cropId,
        canvasId,
        nodeUid,
        photoId,
        filter: 'original',
        rotation: 0,
        zoom: 1,
        photoLoaded: false,
      });
    },
    restoreArrangementCrops(state, { payload }: PayloadAction<RestoreArrangementCropsPayload[]>) {
      const deconstructedCrops: CropEntity[] = payload.map((crop) => {
        const {
          canvas_id: canvasId,
          ordering_upload_id: photoId,
          node_uid: nodeUid,
          blobSrc,
        } = crop;

        const photoCrop = deconstructCropData(crop);

        return {
          ...photoCrop,
          fitCropH: round(photoCrop.cropH * photoCrop.zoom, 4),
          fitCropW: round(photoCrop.cropW * photoCrop.zoom, 4),
          id: utils.buildCropId(canvasId, nodeUid),
          canvasId,
          nodeUid,
          photoId,
          blobSrc,
          restore: true,
          photoLoaded: false,
        };
      });

      let update = cropsAdapter.removeAll(state);
      update = cropsAdapter.addMany(update, deconstructedCrops);
      return update;
    },
    initCropDimensions(state, { payload }: PayloadAction<CropDimensionsPayload>) {
      const { canvasId, nodeUid, nodeDimension, imageDimensions } = payload;
      const { height: imageH, width: imageW } = imageDimensions;
      const cropId = utils.buildCropId(canvasId, nodeUid);

      const crop = state.entities[cropId];

      if (!crop) return state;

      /**
       *
       * Quick patch for when moving from cart to editor with an existing crop
       * The image loads immediately, before the node has been rendered, but there is already enough crop data
       * When node dimension updates have been moved to epics, this will be unnecessary
       */
      if (nodeDimension.height === 0 || nodeDimension.width === 0) {
        return state;
      }

      const { fitCropH, fitCropW } = fitImageToNode(nodeDimension, imageDimensions);

      // If we have fit crop data, and the new fit crop data matches, leave the crops alone
      // Most commonly this happens when loading a filtered image
      if (
        crop.fitCropH &&
        crop.fitCropW &&
        crop.fitCropH === fitCropH &&
        crop.fitCropW === fitCropW
      ) {
        return state;
      }

      // Set up the initial data for a crop
      const cropData = {
        originalW: imageW,
        originalH: imageH,
        nodeAspectRatio: utils.roundAspectRatio(nodeDimension),
        fitCropH,
        cropH: fitCropH,
        fitCropW,
        cropW: fitCropW,
        cropX: 0.5,
        cropY: 0.5,
        zoom: 1,
      };

      cropsAdapter.updateOne(state, {
        id: cropId,
        changes: { ...cropData },
      });
    },
    panCrop(state, { payload }: PayloadAction<PanCropPayload>) {
      const { canvasId, nodeUid, movementX, movementY, flipTrackingDirection } = payload;
      const cropId = utils.buildCropId(canvasId, nodeUid);
      const crop = state.entities[cropId];
      // Certain touch devices (Apple mostly) allow dragging from the edge to navigate browser back/forward
      // This can still trigger the events used to dispatch this action
      // So just return early if we don't have a crop
      if (!crop) return state;

      const { cropX, cropY, cropW, cropH } = crop;
      if (!notNull(cropX) || !notNull(cropY) || !cropW || !cropH) return state;

      let newCropX = cropX;
      let newCropY = cropY;

      if (flipTrackingDirection) {
        newCropX += movementX;
        newCropY += movementY;
      } else {
        newCropX -= movementX;
        newCropY -= movementY;
      }

      return cropsAdapter.updateOne(state, {
        id: cropId,
        changes: {
          cropX: clampCropPanToLimit(cropW, newCropX),
          cropY: clampCropPanToLimit(cropH, newCropY),
        },
      });
    },
    zoomCrop(state, { payload }: PayloadAction<ZoomCropPayload>) {
      if (!state.activeNodeUid) return state;
      const { canvasId } = payload;
      let { zoom } = payload;
      const cropId = utils.buildCropId(canvasId, state.activeNodeUid);
      const crop = state.entities[cropId];
      // Certain touch devices (Apple mostly) allow dragging from the edge to navigate browser back/forward
      // This can still trigger the events used to dispatch this action
      // So just return early if we don't have a crop
      if (!crop) return state;

      const { fitCropH, fitCropW, cropX, cropY } = crop;

      if (!fitCropH || !fitCropW || !cropX || !cropY) return state;

      const newCropH = fitCropH / zoom;
      const newCropW = fitCropW / zoom;

      return cropsAdapter.updateOne(state, {
        id: cropId,
        changes: {
          zoom,
          cropH: newCropH,
          cropW: newCropW,
          cropX: clampCropPanToLimit(newCropW, cropX),
          cropY: clampCropPanToLimit(newCropH, cropY),
        },
      });
    },
    filterCrop(state, { payload }: PayloadAction<FilterCropPayload>) {
      return cropsAdapter.updateOne(state, {
        id: payload.crop.id,
        changes: { filter: payload.filter.filterType, photoLoaded: false },
      });
    },
    rotateCrop(state, { payload }: PayloadAction<RotateCropPayload>) {
      const { cropId, rotation, blobSrc } = payload;
      return cropsAdapter.updateOne(state, {
        id: cropId,
        changes: { rotation, blobSrc },
      });
    },
    updateCropNodeDimensions(
      state,
      { payload }: PayloadAction<Omit<CropDimensionsPayload, 'imageDimensions'>>
    ) {
      const { nodeDimension, canvasId, nodeUid } = payload;
      const cropId = utils.buildCropId(canvasId, nodeUid);
      const crop = state.entities[cropId];
      if (!crop) return state;
      const { originalH, originalW } = crop;
      if (!originalH || !originalW) return state;

      const imageDimensions = { height: originalH, width: originalW };
      const { fitCropH, fitCropW } = fitImageToNode(nodeDimension, imageDimensions);
      const cropData = {
        nodeAspectRatio: utils.roundAspectRatio(nodeDimension),
        fitCropH,
        cropH: fitCropH,
        fitCropW,
        cropW: fitCropW,
        cropX: 0.5,
        cropY: 0.5,
        zoom: 1,
      };

      return cropsAdapter.updateOne(state, { id: cropId, changes: cropData });
    },
    setCropPhotoLoaded(state, { payload }: PayloadAction<CropPhotoLoadedPayload>) {
      return cropsAdapter.updateOne(state, {
        id: payload.id,
        changes: {
          photoLoaded: payload.loaded,
        },
      });
    },
    deleteCrop(state, { payload }: PayloadAction<string>) {
      return cropsAdapter.removeOne(state, payload);
    },
    deleteCropsByPhotoId(state, { payload }: PayloadAction<number>) {
      const cropsToDelete = cropIdsForPhotoId(state.entities, payload);
      return cropsAdapter.removeMany(state, cropsToDelete);
    },
    resetCrops() {
      return cropsAdapter.getInitialState(initialState);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.fetchCropPhoto.fulfilled, (state, { payload }) => {
        const { canvasId, nodeUid, blobSrc } = payload;
        const id = utils.buildCropId(canvasId, nodeUid);

        return cropsAdapter.updateOne(state, {
          id,
          changes: {
            blobSrc,
          },
        });
      })
      .addCase(actions.fetchCropPhoto.rejected, (state, { payload }) => {
        const { canvasId, nodeUid } = payload;
        const id = utils.buildCropId(canvasId, nodeUid);
        return cropsAdapter.removeOne(state, id);
      })
      .addCase(actions.fetchCropFilterPhoto.fulfilled, (state, { payload }) => {
        const { canvasId, nodeUid, blobSrc } = payload;
        const id = utils.buildCropId(canvasId, nodeUid);

        return cropsAdapter.updateOne(state, {
          id,
          changes: {
            blobSrc,
            rotation: 0,
          },
        });
      })
      .addCase(actions.fetchCropFilterPhoto.rejected, (state, { payload }) => {
        const { canvasId, nodeUid } = payload;
        const id = utils.buildCropId(canvasId, nodeUid);

        return cropsAdapter.updateOne(state, {
          id,
          changes: { filter: 'original', photoLoaded: true },
        });
      })
      .addCase(actions.restoreCartProductPhotoCrops.fulfilled, (state, { payload }) => {
        return cropsAdapter.addMany(state, payload);
      })
      .addCase(photoModule.actions.removeManyPhotos.fulfilled, (state) => {
        return cropsAdapter.removeAll(state);
      })
      .addCase(photoModule.actions.removePhoto.fulfilled, (state, { payload }) => {
        const cropsToDelete = cropIdsForPhotoId(state.entities, payload.id);
        return cropsAdapter.removeMany(state, cropsToDelete);
      })
      .addCase(canvases.actions.removeCanvas, (state, { payload }) => {
        const canvasId = payload;

        const removeCropIds = state.ids.filter((cropId) => {
          const { canvasId: cropCanvasId } = utils.parseCropId(cropId as string);
          return cropCanvasId === canvasId;
        });

        return cropsAdapter.removeMany(state, removeCropIds);
      })
      .addCase('arrangements/restoreArrangement', () => {
        return cropsAdapter.getInitialState(initialState);
      })
      .addCase('canvases/resetToSingleCanvas', () => {
        return cropsAdapter.getInitialState(initialState);
      });
  },
});

export default crops;
